import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';
import { ReusableStreamProperty } from '@ipreo/muni-shared';
import { AccountManager, GameDayViewType } from '../../../state/data/state';
import { Tranche } from '../../../gameday/data/gameDay-data.transformations';
import { GameDayRootReducerState } from '../../../state/root.state';
import { trancheDetailsMapper } from '../../mappers/tranche-details.mapper';
import { orderPeriodMapper } from '../../mappers/order-period.mapper';
import {
  getSearchByAccountManagerState,
  getGamedayDealState
} from '../../../state/data/reducer';
import { GameDayModel } from '../../../models/gameday-model';

@Component({
  selector: 'muni-gameday-tranche-details',
  templateUrl: './tranche-details.template.html',
  styleUrls: ['./tranche-details.styles.scss'],
  standalone: false,
})
export class TrancheDetailsComponent {
  private _tranche: Tranche;
  private _viewType: GameDayViewType;
  private readonly tranche$ = new BehaviorSubject<Tranche>(undefined);
  private readonly viewType$ = new BehaviorSubject<GameDayViewType>(undefined);

  @Input()
  public showCloseButton = false;

  @Input()
  public set tranche(tranche: Tranche) {
    this._tranche = tranche;
    this.tranche$.next(tranche);
  }
  public get tranche(): Tranche {
    return this._tranche;
  }

  @Input()
  public set viewType(viewType: GameDayViewType) {
    this._viewType = viewType;
    this.viewType$.next(viewType);
  }
  public get viewType(): GameDayViewType {
    return this._viewType;
  }

  @Output()
  public readonly closeDetails = new EventEmitter<Record<string, unknown>>();

  @ReusableStreamProperty
  public readonly filteredTranche: Observable<Tranche> = combineLatest(
    this.tranche$.pipe(filter((tranche) => !!tranche)),
    this.viewType$
  ).pipe(
    map(([tranche, viewType]) => trancheDetailsMapper(tranche, viewType)),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly isOrderPeriodTerminated: Observable<boolean> = this.store.pipe(
    select(getGamedayDealState),
    filter((deal: GameDayModel.Deal) => !!deal),
    distinctUntilChanged(),
    map((deal: GameDayModel.Deal) => orderPeriodMapper(deal, this.tranche))
  );

  @ReusableStreamProperty
  public readonly entity: Observable<AccountManager> = this.store.pipe(
    select(getSearchByAccountManagerState),
    distinctUntilChanged()
  );

  public readonly isLinkClickable: Observable<boolean> = this.entity.pipe(
    filter((entity: AccountManager) => !!entity),
    map(
      ({ priority }: AccountManager) =>
        priority !== GameDayModel.AccountPriority.brockerDealer
    )
  );

  public get maturities(): GameDayModel.Maturity[] {
    return this.tranche ? this.tranche.maturities : [];
  }

  public get orderCount(): number {
    return this.tranche ? this.tranche.orders.length : 0;
  }

  public get amount(): number {
    return this.tranche ? this.tranche.amount : 0;
  }

  constructor(private store: Store<GameDayRootReducerState>) {}

  public onCloseClick(event: Event): void {
    event.preventDefault();
    this.closeDetails.emit({});
  }
}
